import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useThemeStore } from '../stores/themeStore';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';

interface FilterValues {
  jobTitles: string[];
  locations: string[];
  availabilities: string[];
  skills: string[];
  languages: string[];
  companies: string[];
  initialCompanies: string[];
  employmentStatus?: string;
  consultantId?: string;
  dateRange: {
    start: string;
    end: string;
  };
}

interface BeneficiaireFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterValues) => void;
  currentFilters?: FilterValues;
}

const defaultFilters: FilterValues = {
  jobTitles: [],
  locations: [],
  availabilities: [],
  skills: [],
  languages: [],
  companies: [],
  initialCompanies: [],
  employmentStatus: 'all',
  consultantId: undefined,
  dateRange: {
    start: '',
    end: ''
  }
};

// Liste des titres de postes communs
const commonJobTitles = [
  'Développeur Full Stack',
  'Développeur Front-end',
  'Développeur Back-end',
  'Commercial B2B',
  'Commercial B2C',
  'Assistant(e) administratif',
  'Responsable RH',
  'Chef de projet',
  'Technicien de maintenance',
  'Comptable',
  'Responsable marketing',
  'Ingénieur système',
  'Consultant IT',
  'Business Analyst'
];

interface User {
  id: string;
  name: string;
  email: string;
}

function BeneficiaireFilters({ isOpen, onClose, onApplyFilters, currentFilters = defaultFilters }: BeneficiaireFiltersProps) {
  const [filters, setFilters] = React.useState<FilterValues>(currentFilters || defaultFilters);
  const [customJobTitle, setCustomJobTitle] = React.useState('');
  const [customCompany, setCustomCompany] = React.useState('');
  const [customInitialCompany, setCustomInitialCompany] = React.useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [consultantSearch, setConsultantSearch] = useState('');
  const { darkMode } = useThemeStore();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);

      const usersList: User[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        usersList.push({
          id: doc.id,
          name: data.name || data.email || 'Utilisateur sans nom',
          email: data.email || ''
        });
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const filteredConsultants = users.filter(user =>
    user.name.toLowerCase().includes(consultantSearch.toLowerCase()) ||
    user.email.toLowerCase().includes(consultantSearch.toLowerCase())
  );

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleAddCustomJobTitle = () => {
    if (customJobTitle.trim()) {
      setFilters(prev => ({
        ...prev,
        jobTitles: [...prev.jobTitles, customJobTitle.trim()]
      }));
      setCustomJobTitle('');
    }
  };

  const handleRemoveJobTitle = (title: string) => {
    setFilters(prev => ({
      ...prev,
      jobTitles: prev.jobTitles.filter(t => t !== title)
    }));
  };

  const handleAddCustomCompany = () => {
    if (customCompany.trim()) {
      setFilters(prev => ({
        ...prev,
        companies: [...prev.companies, customCompany.trim()]
      }));
      setCustomCompany('');
    }
  };

  const handleRemoveCompany = (company: string) => {
    setFilters(prev => ({
      ...prev,
      companies: prev.companies.filter(c => c !== company)
    }));
  };

  const handleAddCustomInitialCompany = () => {
    if (customInitialCompany.trim()) {
      setFilters(prev => ({
        ...prev,
        initialCompanies: [...prev.initialCompanies, customInitialCompany.trim()]
      }));
      setCustomInitialCompany('');
    }
  };

  const handleRemoveInitialCompany = (company: string) => {
    setFilters(prev => ({
      ...prev,
      initialCompanies: prev.initialCompanies.filter(c => c !== company)
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center">
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Filtres</h2>
            <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}`}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Filtre par consultant */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Candidats d'un consultant
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Rechercher un consultant (3 premières lettres du prénom)..."
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                  value={consultantSearch}
                  onChange={(e) => setConsultantSearch(e.target.value)}
                />
                {consultantSearch.length > 0 && (
                  <div className={`max-h-48 overflow-y-auto border ${darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200'} rounded-lg`}>
                    {filteredConsultants.length > 0 ? (
                      filteredConsultants.map(user => (
                        <div
                          key={user.id}
                          onClick={() => {
                            setFilters(prev => ({ ...prev, consultantId: user.id }));
                            setConsultantSearch('');
                          }}
                          className={`p-3 cursor-pointer ${darkMode ? 'hover:bg-gray-600 border-b border-gray-600' : 'hover:bg-blue-50 border-b border-gray-100'} last:border-b-0 ${
                            filters.consultantId === user.id ? (darkMode ? 'bg-blue-900' : 'bg-blue-100') : ''
                          }`}
                        >
                          <div className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{user.name}</div>
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.email}</div>
                        </div>
                      ))
                    ) : (
                      <div className={`p-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Aucun consultant trouvé</div>
                    )}
                  </div>
                )}
                {filters.consultantId && (
                  <div className={`flex items-center justify-between p-3 rounded-lg ${darkMode ? 'bg-blue-900' : 'bg-blue-50'}`}>
                    <div>
                      <div className={`font-medium text-sm ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                        {users.find(u => u.id === filters.consultantId)?.name}
                      </div>
                      <div className={`text-xs ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {users.find(u => u.id === filters.consultantId)?.email}
                      </div>
                    </div>
                    <button
                      onClick={() => setFilters(prev => ({ ...prev, consultantId: undefined }))}
                      className={`${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Tapez les 3 premières lettres du prénom du consultant pour le rechercher
                </p>
              </div>
            </div>

            {/* Titre du poste */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Titre du poste
              </label>
              <div className="space-y-4">
                {/* Liste des titres sélectionnés */}
                {filters.jobTitles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {filters.jobTitles.map(title => (
                      <span
                        key={title}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'}`}
                      >
                        {title}
                        <button
                          onClick={() => handleRemoveJobTitle(title)}
                          className={`ml-2 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Sélection des titres communs */}
                <select
                  className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                  onChange={(e) => {
                    if (e.target.value && !filters.jobTitles.includes(e.target.value)) {
                      setFilters(prev => ({
                        ...prev,
                        jobTitles: [...prev.jobTitles, e.target.value]
                      }));
                    }
                    e.target.value = '';
                  }}
                  value=""
                >
                  <option value="">Sélectionner un titre de poste...</option>
                  {commonJobTitles
                    .filter(title => !filters.jobTitles.includes(title))
                    .map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))
                  }
                </select>

                {/* Ajout d'un titre personnalisé */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Autre titre de poste..."
                    className={`flex-1 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                    value={customJobTitle}
                    onChange={(e) => setCustomJobTitle(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomJobTitle();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomJobTitle}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={!customJobTitle.trim()}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>

            {/* Localisation */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Localisation
              </label>
              <select
                multiple
                className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                onChange={(e) => {
                  const values = Array.from(e.target.selectedOptions).map(opt => opt.value);
                  setFilters(prev => ({ ...prev, locations: values }));
                }}
                value={filters.locations}
              >
                {['Marseille', 'Aix-en-Provence', 'Vitrolles', 'Arles', 'Marignane'].map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </div>

            {/* Disponibilité */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Disponibilité
              </label>
              <div className="space-y-2">
                {[
                  'Immédiate',
                  'Sous 15 jours',
                  'Sous 1 mois',
                  'Sous 2 mois',
                  'Sous 3 mois'
                ].map(availability => (
                  <label key={availability} className="flex items-center">
                    <input
                      type="checkbox"
                      className={`rounded border-gray-300 text-blue-600 mr-2 ${darkMode ? 'bg-gray-700 border-gray-600' : ''}`}
                      checked={filters.availabilities.includes(availability)}
                      onChange={(e) => {
                        setFilters(prev => ({
                          ...prev,
                          availabilities: e.target.checked
                            ? [...prev.availabilities, availability]
                            : prev.availabilities.filter(a => a !== availability)
                        }));
                      }}
                    />
                    <span className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{availability}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Statut d'emploi */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Statut d'emploi
              </label>
              <select
                className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                value={filters.employmentStatus || 'all'}
                onChange={(e) => {
                  setFilters(prev => ({
                    ...prev,
                    employmentStatus: e.target.value
                  }));
                }}
              >
                <option value="all">Tous</option>
                <option value="employed">En emploi</option>
                <option value="unemployed">Sans emploi</option>
              </select>
            </div>

            {/* Entreprises */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Entreprise d'embauche
              </label>
              <div className="space-y-4">
                {/* Liste des entreprises sélectionnées */}
                {filters.companies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {filters.companies.map(company => (
                      <span
                        key={company}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-700'}`}
                      >
                        {company}
                        <button
                          onClick={() => handleRemoveCompany(company)}
                          className={`ml-2 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-700'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Ajout d'une entreprise personnalisée */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nom de l'entreprise..."
                    className={`flex-1 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                    value={customCompany}
                    onChange={(e) => setCustomCompany(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomCompany();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomCompany}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={!customCompany.trim()}
                  >
                    Ajouter
                  </button>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Recherche les candidats qui travaillent dans ces entreprises
                </p>
              </div>
            </div>

            {/* Entreprise/Société initiale */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Entreprise/Société initiale
              </label>
              <div className="space-y-4">
                {/* Liste des entreprises initiales sélectionnées */}
                {filters.initialCompanies.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {filters.initialCompanies.map(company => (
                      <span
                        key={company}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-50 text-purple-700'}`}
                      >
                        {company}
                        <button
                          onClick={() => handleRemoveInitialCompany(company)}
                          className={`ml-2 ${darkMode ? 'text-purple-400 hover:text-purple-300' : 'text-purple-500 hover:text-purple-700'}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Ajout d'une entreprise initiale personnalisée */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Nom de l'entreprise/société initiale..."
                    className={`flex-1 rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                    value={customInitialCompany}
                    onChange={(e) => setCustomInitialCompany(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomInitialCompany();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomInitialCompany}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    disabled={!customInitialCompany.trim()}
                  >
                    Ajouter
                  </button>
                </div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Recherche les candidats qui viennent de ces entreprises/sociétés initiales
                </p>
              </div>
            </div>

            {/* Compétences */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Compétences
              </label>
              <input
                type="text"
                placeholder="Séparez les compétences par des virgules"
                className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                value={filters.skills.join(', ')}
                onChange={(e) => {
                  const skills = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                  setFilters(prev => ({ ...prev, skills }));
                }}
              />
            </div>

            {/* Langues */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Langues
              </label>
              <input
                type="text"
                placeholder="Séparez les langues par des virgules"
                className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                value={filters.languages.join(', ')}
                onChange={(e) => {
                  const languages = e.target.value.split(',').map(l => l.trim()).filter(Boolean);
                  setFilters(prev => ({ ...prev, languages }));
                }}
              />
            </div>

            {/* Période d'inscription */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                Période d'inscription
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Du</label>
                  <input
                    type="date"
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                    value={filters.dateRange.start}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, start: e.target.value }
                      }));
                    }}
                  />
                </div>
                <div>
                  <label className={`block text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-1`}>Au</label>
                  <input
                    type="date"
                    className={`w-full rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-700 text-white' : 'border-gray-200'} p-2`}
                    value={filters.dateRange.end}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, end: e.target.value }
                      }));
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-4">
            <button
              onClick={onClose}
              className={`px-4 py-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'} rounded-lg`}
            >
              Annuler
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BeneficiaireFilters;